import { forwardRef, type ReactNode } from 'react';
import {
  OverlayScrollbarsComponent,
  type OverlayScrollbarsComponentRef,
} from 'overlayscrollbars-react';
import { cn } from '@/lib/utils';

interface ScrollContainerProps {
  children: ReactNode;
  className?: string;
  /** Initialize synchronously (false) or during idle time (true). Default: false.
   *  Must be false when paired with useVirtualizer so the viewport element
   *  is available on the first getScrollElement() call. */
  defer?: boolean;
  /** Allow horizontal scrolling when content overflows (default: false — x hidden).
   *  Use for wide content that can get squeezed (e.g. tables when the OptiBot
   *  sidebar narrows the page). */
  allowHorizontal?: boolean;
  /** Keep the scrollbar visible whenever content overflows instead of auto-hiding.
   *  Makes horizontal-scroll affordance discoverable. */
  visibleScrollbar?: boolean;
}

/**
 * Shared scroll container with overlay scrollbars.
 *
 * Wraps OverlayScrollbarsComponent with standardized config:
 * - Auto-hides scrollbar after scrolling stops
 * - Horizontal overflow hidden (vertical-only scrolling)
 * - Theme adapts to light/dark via CSS custom properties in globals.css
 *
 * For virtual scroll integration, use `useScrollViewport(osRef).getViewport`
 * as the `getScrollElement` callback for `useVirtualizer`.
 */
export const ScrollContainer = forwardRef<OverlayScrollbarsComponentRef, ScrollContainerProps>(
  (
    { children, className, defer = false, allowHorizontal = false, visibleScrollbar = false },
    ref,
  ) => (
    <OverlayScrollbarsComponent
      ref={ref}
      defer={defer}
      className={cn('flex-1 min-h-0', className)}
      options={{
        scrollbars: {
          autoHide: visibleScrollbar ? 'never' : 'move',
          autoHideDelay: 800,
        },
        overflow: { x: allowHorizontal ? 'scroll' : 'hidden' },
      }}
    >
      {children}
    </OverlayScrollbarsComponent>
  ),
);

ScrollContainer.displayName = 'ScrollContainer';
